/**
 * Rueckgaengig-Meldung und Strg+Z (F-45) - einmal in der Huelle.
 *
 * Haengt sich an die Aufzeichnung der Datenbank: Jede Loeschung, jedes
 * Abhaken und Umwandeln erscheint als Meldung mit „Rückgängig“. Sie bleibt
 * 8 Sekunden stehen, solange weder Maus noch Fokus auf ihr liegen - und
 * solange kein modaler Dialog offen ist: Dessen Top-Layer liegt ueber der
 * Meldung, sie waere nicht bedienbar. Nach dem Schliessen laeuft sie neu an.
 *
 * Screenreader: Angesagt wird ueber eine dauerhaft vorhandene Live-Region,
 * in der nur der Text wechselt - eine mit der Meldung neu eingehaengte
 * Region wird nicht zuverlaessig vorgelesen.
 *
 * Strg+Z (Cmd+Z) nimmt die letzte Aktion zurueck - aber nicht beim Tippen
 * (dort gehoert die Taste dem Textfeld), nicht bei offenem Dialog und ohne
 * gemerkte Aktion gar nicht (dann auch kein preventDefault). Checkboxen,
 * Radios und Auswahllisten zaehlen nicht als Textfeld (kein eigenes
 * Rueckgaengig): Abhaken oder „Tag leeren“ im Menue und gleich Strg+Z soll gehen.
 */
import { useEffect, useState } from 'react';

import { db } from '@/db/schema';
import { Toast } from '@/ui/composites/Toast';
import { istRueckgaengigTaste, useRueckgaengig } from '@/modules/papierkorb/rueckgaengig';
import { de } from '@/i18n/de';

export const SICHTBAR_MS = 8000;

/** Hier gehoert Strg+Z dem Browser (Text zuruecknehmen). */
const EINGABE = 'textarea, [contenteditable]:not([contenteditable="false"]), '
  + 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])';

const dialogOffen = (): boolean => document.querySelector('dialog[open]') !== null;

/** Ist gerade ein Dialog offen? Beobachtet `open` und das Ein- und Aushaengen. */
function useDialogOffen(): boolean {
  const [offen, setOffen] = useState(dialogOffen);
  useEffect(() => {
    const pruefen = () => setOffen(dialogOffen());
    const beobachter = new MutationObserver(pruefen);
    beobachter.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    pruefen();
    return () => beobachter.disconnect();
  }, []);
  return offen;
}

export function RueckgaengigHost() {
  const anzeige = useRueckgaengig((s) => s.anzeige);
  const laeuft = useRueckgaengig((s) => s.laeuft);
  const rueckgaengigMachen = useRueckgaengig((s) => s.rueckgaengigMachen);
  const schliessen = useRueckgaengig((s) => s.schliessen);
  const imDialog = useDialogOffen();
  // Gehalten wird immer genau eine Meldung - eine neue laeuft wieder ab.
  const [gehalten, setGehalten] = useState<number | null>(null);
  const haelt = anzeige !== null && (gehalten === anzeige.nr || imDialog);

  useEffect(() => {
    db.aufzeichnung.beiAktion = (m) => useRueckgaengig.getState().merken(m);
    return () => { db.aufzeichnung.beiAktion = null; };
  }, []);

  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (!istRueckgaengigTaste(e)) return;
      if (e.target instanceof Element && e.target.closest(EINGABE)) return;
      if (dialogOffen()) return;
      const s = useRueckgaengig.getState();
      if (!s.stapel.length) return;
      e.preventDefault();
      void s.rueckgaengigMachen();
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, []);

  useEffect(() => {
    if (!anzeige || haelt) return;
    const id = window.setTimeout(() => {
      // Nur diese Meldung schliessen, nicht eine inzwischen neuere.
      if (useRueckgaengig.getState().anzeige?.nr === anzeige.nr) schliessen();
    }, SICHTBAR_MS);
    return () => window.clearTimeout(id);
  }, [anzeige, haelt, schliessen]);

  const aktion = anzeige?.aktion;
  return (
    <>
      {/* Hinter einem modalen Dialog ist alles andere stumm - angesagt wird nach dem Schliessen. */}
      <p role="status" className="sr-only">{anzeige && !imDialog ? anzeige.text : ''}</p>
      {anzeige && (
        <Toast
          text={anzeige.text}
          fehler={anzeige.fehler}
          aktion={aktion && {
            label: de.papierkorb.rueckgaengig,
            titel: de.papierkorb.rueckgaengigTitel,
            disabled: laeuft,
            onClick: () => void rueckgaengigMachen(aktion),
          }}
          onSchliessen={schliessen}
          schliessenLabel={de.papierkorb.meldungSchliessen}
          onHalten={(h) => setGehalten(h ? anzeige.nr : null)}
        />
      )}
    </>
  );
}
